(ns frontend.view.grid.screening.events
  (:require
   [potpuri.core :as potpuri]
   [re-frame.core :as rf]
   [frontend.conn :as conn]
   [reagent.core :as r]
   [promesa.core :as p]
   [frontend.state.current-session :as session]
   [frontend.view.grid.screening.db :as db]
   [frontend.db :as app-db]
   [frontend.react.virtualized.scrollable-grid.common :as grid]
   [frontend.local-storage :as ls]
   [common.schema.registry :as registry]
   [common.localization :as loc]
   [common.util :as cu]
   [frontend.view.modals.events :as modals]
   [potpuri.core :refer [index-by fn-> fn->>]]
   [frontend.actions.named-view :as named-view-actions]
   [frontend.view.grid.investors-grid.events :as investor-events]
   [frontend.logging :as logging]
   [frontend.utils :as fu]
   [frontend.state.permissions :as f-permissions]
   [frontend.view.modals.confirmation.view :as confirmation]
   [frontend.tenant-customizations :as tenant-customizations]
   [frontend.zap-time :as zap-time]
   [frontend.exports.excel :as excel]
   [common.schema.entity-types :as entity-types]
   [common.schema.entities.workspace.kyc.kyc-utils :as kyc-utils]
   [common.timer :as timer]
   [frontend.exports.exceljs.workbook :as ex-workbook]
   [common.timezones :as timezones]
   [common.date-formats :as date-formats]
   [frontend.actions.screening-profile :as sp-actions]
   [common.schema.entities.workspace.kyc.templates.template-registry :as template-registry]
   [frontend.view.grid.screening.columns :as columns]))

(defn ls-path
  [& rest]
  (concat
   [(:db/id @session/account)
    (:db/id @session/tenant)
    :screening-profiles-view-settings]
   rest))

(defn precalculated-schema
  [recalculate?]
  (let [calculated @db/schema
        tenant-schema @session/tenant-schema
        schema
        (cond
          (and (not recalculate?)
               calculated)
          calculated
          :else
          (->>
           (registry/get-schema @session/tenant-schema :entity.type/screening-profile)
           (remove
            (fn [[k v]]
              (let [grid-visibility (->> v
                                         :field/default-visibility
                                         :grid)]
                (or (nil? grid-visibility)
                    (= grid-visibility :editor.type/none)
                    (if (or (not= (:field/data-type v) :data.type/currency-symbol)
                            (not (:field/secondary-field? v)))
                      (not (f-permissions/field-read-access? k :entity.type/screening-profile nil))
                      (not (f-permissions/field-read-access? (:field/related-fields v)
                                                             :entity.type/screening-profile
                                                             nil)))))))
           (sort
            (fn [[attribute-a _] [attribute-b _]]
              (cond ;; show name first
                (= :screening-profile/name attribute-a)
                -1
                (= :screening-profile/name attribute-b)
                1
                :else
                (compare (let [fs (registry/get-field-schema tenant-schema attribute-a)]
                           (loc/field-name attribute-a fs))
                         (let [fs (registry/get-field-schema tenant-schema attribute-b)]
                           (loc/field-name attribute-b fs))))))
           (doall)))
        schema (if (and (not recalculate?)
                        calculated)
                 schema
                 (concat schema))]
    (when (or recalculate?
              (not calculated))
      (reset! db/schema schema))
    schema))

(def column-keys
  [:screening-profile/name
   :screening-profile/screening-status
   :screening-profile/sanction-status
   :screening-profile/pep-status
   :screening-profile/latest-pep-status-change
   :screening-profile/rca-status
   :screening-profile/latest-rca-status-change
   :screening-profile/external-screening-id
   :screening-profile/refresh-timestamp
   :screening-profile/type
   :screening-profile/company-name
   :screening-profile/first-name
   :screening-profile/last-name
   :screening-profile/country
   :screening-profile/date-of-birth
   :screening-profile/uuid
   :screening-profile/kyc-profile-labels
   :screening-profile/counterparty-diligence-labels
   :screening-profile/business-id
   :screening-profile/pep-or-rca-mismatch])

(defn system-views
  []
  [{:named-view/settings {:selected-columns column-keys
                          :ordered-columns  column-keys
                          :sort-by          :screening-profile/name
                          :sort-direction   "ASC"
                          :view-mode        :grid-mode}
    :named-view/name "All Screening profiles"
    :disable-delete? true
    :db/id "named-view-all-screening-profiles"}
   {:named-view/settings {:selected-columns column-keys
                          :ordered-columns  column-keys
                          :sort-by          :screening-profile/name
                          :sort-direction   "ASC"
                          :view-mode        :board-mode
                          :filters          {:screening-profile/type
                                             {:operand :operand/any-of
                                              :lower-bound-value #{:screening-profile-type/person}}}}
    :named-view/name "Person Screening profiles"
    :disable-delete? true
    :db/id "named-view-person-screening-profiles"}
   {:named-view/settings {:selected-columns column-keys
                          :ordered-columns  column-keys
                          :sort-by          :screening-profile/name
                          :sort-direction   "ASC"
                          :view-mode        :board-mode
                          :filters          {:screening-profile/type
                                             {:operand :operand/any-of
                                              :lower-bound-value #{:screening-profile-type/company}}}}
    :named-view/name "Company Screening profiles"
    :disable-delete? true
    :db/id "named-view-company-screening-profiles"}
   {:named-view/settings {:selected-columns column-keys
                          :ordered-columns  column-keys
                          :sort-by          :screening-profile/name
                          :sort-direction   "ASC"
                          :view-mode        :grid-mode
                          :filters          {:screening-profile/screening-status
                                             {:operand :operand/any-of
                                              :lower-bound-value #{:screening-status/active}}}}
    :named-view/name "Active screening"
    :disable-delete? true
    :db/id "named-active-screening-profiles"}
   {:named-view/settings {:selected-columns column-keys
                          :ordered-columns  column-keys
                          :sort-by          :screening-profile/name
                          :sort-direction   "ASC"
                          :view-mode        :board-mode
                          :filters          {:screening-profile/screening-status
                                             {:operand :operand/any-of
                                              :lower-bound-value #{:screening-status/inactive}}}}
    :named-view/name "Inactive screening"
    :disable-delete? true
    :db/id "named-inactive-screening-profiles"}
   {:named-view/settings {:selected-columns column-keys
                          :ordered-columns  column-keys
                          :sort-by          :screening-profile/name
                          :sort-direction   "ASC"
                          :view-mode        :board-mode
                          :filters          {:screening-profile/sanction-status
                                             {:operand :operand/any-of
                                              :lower-bound-value #{:sanction-status/green}}}}
    :named-view/name "Green Sanction status"
    :disable-delete? true
    :db/id "named-green-screening-profiles"}
   {:named-view/settings {:selected-columns column-keys
                          :ordered-columns  column-keys
                          :sort-by          :screening-profile/name
                          :sort-direction   "ASC"
                          :view-mode        :board-mode
                          :filters          {:screening-profile/sanction-status
                                             {:operand :operand/any-of
                                              :lower-bound-value #{:sanction-status/yellow}}}}
    :named-view/name "Yellow Sanction status"
    :disable-delete? true
    :db/id "named-yellow-screening-profiles"}
   {:named-view/settings {:selected-columns column-keys
                          :ordered-columns  column-keys
                          :sort-by          :screening-profile/name
                          :sort-direction   "ASC"
                          :view-mode        :board-mode
                          :filters          {:screening-profile/sanction-status
                                             {:operand :operand/any-of
                                              :lower-bound-value #{:sanction-status/red}}}}
    :named-view/name "Red Sanction status"
    :disable-delete? true
    :db/id "named-red-screening-profiles"}])

;;precalculate for performance reasons
(defn keys-and-schemas
  []
  (precalculated-schema false))

(defn screening-profiles-column-keys
  []
  (let [schemas (keys-and-schemas)
        col-keys (concat
                  (when (session/is-admin?) [:db/id])
                  (map first schemas))]
    col-keys))

(def default-kanban-attributes [:screening-profile/name :screening-profile/screening-status])

(defn visible-keys-and-schemas
  []
  (let [{:keys [attributes footer-1 footer-2]} @db/card-settings
        visible-attributes
        (set (concat @db/selected-columns
                     attributes
                     default-kanban-attributes
                     [footer-1 footer-2
                      (entity-types/type->owner-attribute :entity.type/screening-profile)]))]
    (->> (keys-and-schemas)
         (filter (fn [[k _fs]]
                   (some #{k} visible-attributes))))))

(declare expanded? toggle-expand!)

;;necessary arguments to use common grid functions
(defn setup-args
  []
  {:state db/screening-profiles
   :entity-type :entity.type/screening-profile
   :selected-columns-state db/selected-columns
   :ordered-columns-state db/ordered-columns
   :filters-state db/screening-profile-filters
   :view-settings-state db/view-settings
   :column-widths-state db/column-widths
   :selected-pipeline nil
   :ls-path ls-path
   :kanban? true
   :kanban-args nil
   #_{:default-grouped-by  :kyc-profile/status
      :kanban-state        db/kanban
      :grouped-by-state    db/grouped-by
      :card-settings-state db/card-settings
      ;;for long card view:
      :default-attributes  default-kanban-attributes
      :expanded-state      db/expanded
      :sorted-kanban-entities-state db/sorted-kanban-entities
      :lists-state         db/lists
      :expanded?           expanded?
      :toggle-expand!      toggle-expand!}
   :column-keys screening-profiles-column-keys
   :default-columns columns/default-columns
   :mandatory? columns/mandatory?
   :visible-keys-and-schemas-fn visible-keys-and-schemas})

(defn expanded?
  [entity-id]
  (let [card-mode (or (:card-mode @db/kanban)
                      :short-card)
        individual (get @db/expanded entity-id)]
    (or (and (= card-mode :short-card)
             individual)
        (and (= card-mode :long-card)
             (not individual)))))

(defn toggle-expand!
  [entity-id]
  (let [expanded? (get @db/expanded entity-id)]
    (if expanded?
      (swap! db/expanded dissoc entity-id)
      (swap! db/expanded assoc entity-id true))))

(defn resolve-column-key-universe
  []
  (grid/resolve-column-key-universe (setup-args)))

(defn resolve-column-keys
  []
  (grid/resolve-column-keys (setup-args)))



(defn clear-screening-profiles-from-app-db
  []
  (let [screening-profile-ids (->> @session/all-screening-profiles
                                   (map :db/id))
        entities @session/entities

        entities-without-screening-profiles
        (apply dissoc entities screening-profile-ids)]
    (reset! session/entities entities-without-screening-profiles)))

(defn cleanup
  []
  (when-let [tracker-1 (:tracker-1 @db/screening-profiles)]
    (r/dispose! tracker-1))
  (when-let [tracker-2 (:tracker-2 @db/screening-profiles)]
    (r/dispose! tracker-2))
  (when-let [tracker-3 (:tracker-3 @db/screening-profiles)]
    (r/dispose! tracker-3))
  (swap! db/screening-profiles dissoc
         :loaded
         :schema
         :entities
         :entity-types
         :view-settings
         :sorted-grid-entities
         :search
         :kanban
         :sorted-kanban-entities
         :selected
         :grid-entities)
  (clear-screening-profiles-from-app-db))

(defn toggle-quick-filters!
  []
  (swap! db/screening-profiles update :show-quick-filters? not))

(defn system-views-by-id
  []
  (->> (system-views)
       (map (fn [v]
              [(:db/id v) v]))
       (into {})))

(defn named-views
  []
  (concat (system-views)
          (->> @session/all-named-views
               (cu/only-entity :named-view/view :screening-grid)
               (sort-by :common/created-on))))

(defn named-view-by-id
  [id]
  (or (get @session/entities id)
      (get (system-views-by-id) id)))

(defn switch-mode!
  [current-mode]
  (let [new-mode (if (= current-mode :grid-mode) :board-mode :grid-mode)]
    (swap! db/view-settings assoc :view-mode new-mode)
    (ls/update-in (ls-path) assoc :view-mode new-mode)))

(defn setup-column-state!
  [view-settings]
  (reset! db/view-settings view-settings)
  (when (nil? @db/selected-columns)
    (let [default-view-settings (->> (named-views)
                                     first
                                     :named-view/settings)]
      (reset! db/view-settings default-view-settings)
      (ls/set-in (ls-path) default-view-settings)))
  (grid/recalculate (assoc (setup-args) :re-sort? true)))

(defn load-view!
  [named-view] ;;named-view entity
  (let [{:keys [named-view/settings]} named-view]
    (ls/set-in (ls-path) settings)
    (setup-column-state! settings)))

(defn save-current-view!
  [name]
  (let [settings @db/view-settings
        msg {:named-view/name     name
             :named-view/view     :screening-grid
             :named-view/settings settings}]
    (named-view-actions/*create-named-view->
     msg
     identity
     (fn [& a] ;;TODO show error message
       (logging/error "create-named-view failed" a)))))

(defn screening-profile-updates
  []
  (swap! db/screening-profiles assoc :grid-entities @session/all-screening-profiles))

(defn permission-updates
  []
  (let [current-permissions @session/current-permissions]
    (when current-permissions
      (precalculated-schema true))))

(defn trigger-recalculation
  []
  (let [permissions-trigger @db/schema
        entities-trigger @db/grid-entities]
    (when (or permissions-trigger entities-trigger)
      (fu/timeout 10
                  (fn []
                    (grid/recalculate (assoc (setup-args) :stale-sort? true))
                    (swap! db/screening-profiles assoc :loaded true)
                    (deref db/screening-profiles))))))

(defn setup
  [closed-atom?]
  (when-not @closed-atom?
    (sp-actions/*->get-all-screening-profiles
     (fn [response]
       (let [screening-profiles (->> response
                                     :body
                                     :screening-profiles
                                     (map (fn [screening-profile]
                                            [(:db/id screening-profile)
                                             screening-profile]))
                                     (into {}))]
         (swap! app-db/entities merge screening-profiles)
         (if (empty? (ls/get-in (ls-path)))
           (load-view! (first (named-views)))
           (reset! db/view-settings (ls/get-in (ls-path))))
         (swap! db/screening-profiles assoc
                :tracker-1 (r/track! screening-profile-updates)
                :tracker-2 (r/track! permission-updates)
                :tracker-3 (r/track! trigger-recalculation))
         (setup-column-state! (ls/get-in (ls-path)))))
     (fn [error]
       (js/console.log "error getting screening profiles " error)))))



;; Reframe

(rf/reg-event-db
 ::open-modal
 (fn [db [_ screening-profile-id]]
   (assoc-in db [:modals :screening-profile] screening-profile-id)))

(rf/reg-event-db
 ::close-modal
 (fn [db _]
   (potpuri/dissoc-in db [:modals :screening-profile])))
